import React, { useEffect, useMemo, useState } from 'react';
import { addDays, eachDayOfInterval, format, parseISO, startOfWeek } from 'date-fns';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Popover from '@mui/material/Popover';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import { CREW_ASSIGNMENTS } from '../../data/projectsData';
import { TODAY, assignmentsForJob, colorForJob, workTypeLabel } from '../../utils/projectsUtils';

interface DraftAssignment {
  id: number;
  order_no: string;
  emp_id: string;
  tech_name: string;
  date: string;
  start_time: string;
  duration: number;
  work_type: string;
}

type DragPayload = { kind: 'tech'; id: number } | { kind: 'day'; date: string };

interface JobScheduleBoardProps {
  jobId: string;
}

const seedDrafts = (jobId: string): DraftAssignment[] =>
  assignmentsForJob(jobId)
    .filter((assignment) => assignment.date)
    .map((assignment, index) => ({
      id: index,
      order_no: assignment.order_no,
      emp_id: assignment.emp_id,
      tech_name: assignment.tech_name,
      date: assignment.date as string,
      start_time: assignment.start_time,
      duration: assignment.duration,
      work_type: assignment.work_type,
    }));

// External conflict: same tech booked the same day on a DIFFERENT job (current ServMan assignments).
const externalConflict = (empId: string, date: string, jobId: string): string | null => {
  const clash = CREW_ASSIGNMENTS.find(
    (assignment) => assignment.emp_id === empId && assignment.date === date && assignment.job_id !== jobId,
  );
  return clash ? `On #${clash.order_no} (${clash.job_id})` : null;
};

const JobScheduleBoard: React.FC<JobScheduleBoardProps> = ({ jobId }) => {
  const [drafts, setDrafts] = useState<DraftAssignment[]>(() => seedDrafts(jobId));
  const [weekAnchor, setWeekAnchor] = useState<Date>(() => startOfWeek(parseISO(TODAY)));
  const [dragOverKey, setDragOverKey] = useState<string | null>(null);
  const [reschedule, setReschedule] = useState<{
    anchorEl: HTMLElement;
    kind: 'tech' | 'day';
    id?: number;
    sourceDate: string;
  } | null>(null);
  const [pickDate, setPickDate] = useState<string>('');

  useEffect(() => {
    setDrafts(seedDrafts(jobId));
    setWeekAnchor(startOfWeek(parseISO(TODAY)));
  }, [jobId]);

  const days = useMemo(() => eachDayOfInterval({ start: weekAnchor, end: addDays(weekAnchor, 13) }), [weekAnchor]);
  const techs = useMemo(() => {
    const map = new Map<string, string>();
    seedDrafts(jobId).forEach((d) => map.set(d.emp_id, d.tech_name));
    drafts.forEach((d) => map.set(d.emp_id, d.tech_name));
    return Array.from(map.entries())
      .map(([emp_id, name]) => ({ emp_id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [drafts, jobId]);

  const original = useMemo(() => seedDrafts(jobId), [jobId]);
  const movedCount = drafts.filter((d) => {
    const orig = original.find((o) => o.id === d.id);
    return orig && (orig.date !== d.date || orig.emp_id !== d.emp_id);
  }).length;

  const jobColor = colorForJob(jobId);

  const moveTech = (id: number, targetDate: string, targetEmpId: string, targetTechName: string) => {
    setDrafts((prev) =>
      prev.map((d) => (d.id === id ? { ...d, date: targetDate, emp_id: targetEmpId, tech_name: targetTechName } : d)),
    );
  };

  const moveCrewDay = (sourceDate: string, targetDate: string) => {
    if (sourceDate === targetDate) return;
    setDrafts((prev) => prev.map((d) => (d.date === sourceDate ? { ...d, date: targetDate } : d)));
  };

  // Click-to-reschedule (to any date, including outside the visible window).
  const applyReschedule = () => {
    if (!reschedule || !pickDate || pickDate === reschedule.sourceDate) {
      setReschedule(null);
      return;
    }
    if (reschedule.kind === 'tech' && reschedule.id != null) {
      const item = drafts.find((d) => d.id === reschedule.id);
      if (item) moveTech(item.id, pickDate, item.emp_id, item.tech_name);
    } else {
      moveCrewDay(reschedule.sourceDate, pickDate);
    }
    setWeekAnchor(startOfWeek(parseISO(pickDate))); // jump the window to reveal the moved crew
    setReschedule(null);
  };

  const handleDropOnCell = (event: React.DragEvent, targetDate: string, tech: { emp_id: string; name: string }) => {
    event.preventDefault();
    setDragOverKey(null);
    const payload: DragPayload = JSON.parse(event.dataTransfer.getData('application/json'));
    if (payload.kind === 'tech') moveTech(payload.id, targetDate, tech.emp_id, tech.name);
  };

  const handleDropOnDay = (event: React.DragEvent, targetDate: string) => {
    event.preventDefault();
    setDragOverKey(null);
    const payload: DragPayload = JSON.parse(event.dataTransfer.getData('application/json'));
    if (payload.kind === 'day') moveCrewDay(payload.date, targetDate);
  };

  const cellAssignments = (empId: string, isoDate: string) =>
    drafts.filter((d) => d.emp_id === empId && d.date === isoDate);

  const crewCountForDay = (isoDate: string) => drafts.filter((d) => d.date === isoDate).length;

  return (
    <Card variant="outlined" sx={{ overflowX: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1.5, gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton size="small" onClick={() => setWeekAnchor((w) => addDays(w, -7))}>
            <ChevronLeftIcon />
          </IconButton>
          <Typography variant="subtitle1" fontWeight={600} sx={{ minWidth: 220, textAlign: 'center' }}>
            {format(weekAnchor, 'MMM d')} – {format(addDays(weekAnchor, 13), 'MMM d, yyyy')}
          </Typography>
          <IconButton size="small" onClick={() => setWeekAnchor((w) => addDays(w, 7))}>
            <ChevronRightIcon />
          </IconButton>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {movedCount > 0 && (
            <Chip size="small" color="warning" label={`${movedCount} unsaved move${movedCount === 1 ? '' : 's'}`} />
          )}
          <Button
            size="small"
            startIcon={<RestartAltIcon />}
            disabled={movedCount === 0}
            onClick={() => setDrafts(original)}
          >
            Reset
          </Button>
          <Tooltip title="Writing crew changes back to ServMan (ORDEMPS/SCHEDULE) isn’t available yet">
            <span>
              <Button size="small" variant="contained" color="success" disabled>
                Save to ServMan
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>

      <Alert severity="info" icon={<DragIndicatorIcon />} sx={{ mx: 1.5, mb: 1 }}>
        Drag within the view to reschedule, or <strong>click a day header</strong> (moves the whole crew) or a{' '}
        <strong>technician chip</strong> (moves one person) to send it to any date.
      </Alert>

      <Box sx={{ display: 'grid', gridTemplateColumns: `minmax(120px, 150px) repeat(14, minmax(56px, 1fr))` }}>
        {/* Header row: blank corner + draggable day headers */}
        <Box
          sx={{
            p: 1,
            borderBottom: 1,
            borderColor: 'divider',
            backgroundColor: 'grey.100',
            position: 'sticky',
            left: 0,
            zIndex: 3,
          }}
        >
          <Typography variant="caption" fontWeight={700}>
            Technician
          </Typography>
        </Box>
        {days.map((day) => {
          const isoDate = format(day, 'yyyy-MM-dd');
          const count = crewCountForDay(isoDate);
          const isToday = isoDate === TODAY;
          return (
            <Box
              key={isoDate}
              draggable={count > 0}
              onDragStart={(event) =>
                event.dataTransfer.setData('application/json', JSON.stringify({ kind: 'day', date: isoDate }))
              }
              onDragOver={(event) => {
                event.preventDefault();
                setDragOverKey(`day:${isoDate}`);
              }}
              onDragLeave={() => setDragOverKey(null)}
              onDrop={(event) => handleDropOnDay(event, isoDate)}
              onClick={(event) => {
                if (count > 0) {
                  setReschedule({ anchorEl: event.currentTarget, kind: 'day', sourceDate: isoDate });
                  setPickDate(isoDate);
                }
              }}
              sx={{
                p: 1,
                borderBottom: 2,
                borderLeft: 1,
                borderColor: isToday ? 'primary.main' : 'divider',
                backgroundColor: dragOverKey === `day:${isoDate}` ? 'primary.50' : 'grey.100',
                cursor: count > 0 ? 'pointer' : 'default',
                textAlign: 'center',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                {count > 0 && <DragIndicatorIcon sx={{ fontSize: 14, color: 'text.disabled' }} />}
                <Typography
                  variant="caption"
                  fontWeight={isToday ? 700 : 600}
                  color={isToday ? 'primary.main' : 'inherit'}
                >
                  {format(day, 'EEE M/d')}
                </Typography>
              </Box>
              {count > 0 && (
                <Typography variant="caption" color="text.secondary">
                  {count} on crew
                </Typography>
              )}
            </Box>
          );
        })}

        {/* Tech rows */}
        {techs.map((tech) => (
          <React.Fragment key={tech.emp_id}>
            <Box
              sx={{
                p: 1,
                borderBottom: 1,
                borderColor: 'divider',
                position: 'sticky',
                left: 0,
                zIndex: 1,
                backgroundColor: 'background.paper',
              }}
            >
              <Typography variant="body2" fontWeight={600}>
                {tech.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {tech.emp_id}
              </Typography>
            </Box>
            {days.map((day) => {
              const isoDate = format(day, 'yyyy-MM-dd');
              const cellKey = `cell:${tech.emp_id}:${isoDate}`;
              const items = cellAssignments(tech.emp_id, isoDate);
              return (
                <Box
                  key={isoDate}
                  onDragOver={(event) => {
                    event.preventDefault();
                    setDragOverKey(cellKey);
                  }}
                  onDragLeave={() => setDragOverKey(null)}
                  onDrop={(event) => handleDropOnCell(event, isoDate, tech)}
                  sx={{
                    p: 0.5,
                    minHeight: 52,
                    borderBottom: 1,
                    borderLeft: 1,
                    borderColor: 'divider',
                    backgroundColor: dragOverKey === cellKey ? 'primary.50' : 'transparent',
                  }}
                >
                  {items.map((item) => {
                    const conflict = externalConflict(item.emp_id, item.date, jobId);
                    return (
                      <Tooltip
                        key={item.id}
                        arrow
                        title={
                          conflict ? `Conflict: ${conflict}` : `${workTypeLabel(item.work_type)} · #${item.order_no}`
                        }
                      >
                        <Box
                          draggable
                          onDragStart={(event) =>
                            event.dataTransfer.setData(
                              'application/json',
                              JSON.stringify({ kind: 'tech', id: item.id }),
                            )
                          }
                          onClick={(event) => {
                            event.stopPropagation();
                            setReschedule({
                              anchorEl: event.currentTarget,
                              kind: 'tech',
                              id: item.id,
                              sourceDate: item.date,
                            });
                            setPickDate(item.date);
                          }}
                          sx={{
                            backgroundColor: jobColor,
                            color: 'white',
                            borderRadius: 1,
                            px: 0.75,
                            py: 0.25,
                            mb: 0.5,
                            fontSize: 12,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            border: conflict ? '2px solid #ffb300' : 'none',
                          }}
                        >
                          {conflict && <WarningAmberIcon sx={{ fontSize: 13 }} />}
                          {item.start_time} · {item.duration}h
                        </Box>
                      </Tooltip>
                    );
                  })}
                </Box>
              );
            })}
          </React.Fragment>
        ))}
      </Box>

      <Popover
        open={Boolean(reschedule)}
        anchorEl={reschedule?.anchorEl ?? null}
        onClose={() => setReschedule(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: 240 }}>
          <Typography variant="subtitle2">
            {reschedule?.kind === 'day' ? 'Move the whole crew to a new date' : 'Reschedule technician to a new date'}
          </Typography>
          <TextField
            type="date"
            size="small"
            label="New date"
            value={pickDate}
            onChange={(event) => setPickDate(event.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button size="small" onClick={() => setReschedule(null)}>
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              disabled={!pickDate || pickDate === reschedule?.sourceDate}
              onClick={applyReschedule}
            >
              Move
            </Button>
          </Box>
        </Box>
      </Popover>
    </Card>
  );
};

export default JobScheduleBoard;
